import { AuthenticatedHttp } from '@/http/axios';
import { sanitizeCnpj } from '@/utils/cnpj';
import { BUSINESS_DOCUMENT_TYPE, BUSINESS_KIND, parseBusinessUiStatus } from '@/consts/businesses';

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const resolveDocumentType = (raw) => {
  const documentType = String(pickFirst(raw.document_type, raw.documentType) ?? '').toLowerCase();

  return documentType === BUSINESS_DOCUMENT_TYPE.CNPJ
    ? BUSINESS_DOCUMENT_TYPE.CNPJ
    : BUSINESS_DOCUMENT_TYPE.CPF;
};

// O backend so manda rejection em negocio reprovado, e o reason so vem quando o
// analista permitiu a correcao (CBK-540). can_fix ausente conta como "nao pode",
// que e o padrao da rejeicao no Painel Admin (CBK-526 4.1 RF-02).
const normalizeRejection = (rejection) => {
  if (!rejection || typeof rejection !== 'object') {
    return null;
  }

  return {
    reason: pickFirst(rejection.reason) ?? null,
    howToFix: pickFirst(rejection.how_to_fix, rejection.howToFix) ?? null,
    complement: pickFirst(rejection.complement) ?? null,
    canFix: Boolean(pickFirst(rejection.can_fix, rejection.canFix)),
    standardized: Boolean(pickFirst(rejection.standardized)),
  };
};

export const normalizeBusiness = (raw = {}) => {
  const documentType = resolveDocumentType(raw);
  const isCompany = documentType === BUSINESS_DOCUMENT_TYPE.CNPJ;
  const displayName = pickFirst(raw.display_name, raw.displayName) ?? '';
  const legalName = pickFirst(raw.legal_name, raw.legalName) ?? '';
  const documentFormatted = pickFirst(raw.document_formatted, raw.documentFormatted) ?? '';
  const isPersonal =
    String(pickFirst(raw.kind) ?? BUSINESS_KIND.PERSONAL).toLowerCase() !==
    BUSINESS_KIND.ADDITIONAL;
  const status = pickFirst(raw.status) ?? '';
  const billingStatus = pickFirst(raw.billing_status, raw.billingStatus) ?? '';

  return {
    id: pickFirst(raw.id) ?? '',
    slug: pickFirst(raw.slug) ?? '',
    documentType,
    isCompany,
    isPersonal,
    hasDocument: Boolean(documentFormatted),
    name: (isCompany ? legalName || displayName : displayName) || '',
    documentLabel: documentFormatted
      ? `${documentType.toUpperCase()} ${documentFormatted}`
      : '',
    status,
    billingStatus,
    // Sem ui_status o negocio nao tem estado: e conta em rascunho, sem cadastro
    // concluido. Inventar "Em analise" aqui travava o painel e escondia a Home
    // que leva a pessoa para o pre-cadastro.
    uiStatus: parseBusinessUiStatus(pickFirst(raw.ui_status, raw.uiStatus)),
    isDefault: Boolean(pickFirst(raw.is_default, raw.isDefault) ?? false),
    rejection: normalizeRejection(raw.rejection),
  };
};

// A lista sai sem o X-Cakto-Business de proposito: ela e do usuario, nao do
// negocio, e assim continua respondendo mesmo que o negocio guardado esteja
// invalido ou que o header esteja sendo barrado, deixando o usuario trocar de
// negocio para se recuperar.
// Sem as waffle flags a rota nao existe para a conta, e isso nao e falha: o
// painel segue como antes, sem seletor. Botar o erro na barra lateral poria um
// aviso com "Tentar novamente" em todo usuario, numa tentativa que nunca vai
// funcionar. Falha de verdade continua subindo.
const FEATURE_UNAVAILABLE_STATUS = [403, 404];

export const getBusinesses = async () => {
  try {
    const { data } = await AuthenticatedHttp.get('/businesses/');
    const list = Array.isArray(data) ? data : data?.results;

    return (list ?? []).map(normalizeBusiness);
  } catch (error) {
    if (FEATURE_UNAVAILABLE_STATUS.includes(error?.response?.status)) {
      return [];
    }

    throw error;
  }
};

// O backend trata o DELETE como fechamento do negocio, nao exclusao fisica.
export const closeBusiness = async ({ businessId }) =>
  AuthenticatedHttp.delete(`/businesses/${businessId}/`).then(({ data }) => data);

// O validate responde 200 com valid:false e o motivo em errors, entao sem este
// lanse o front trataria recusa como sucesso e a tela ficava muda.
export const validateBusinessCnpj = async ({ cnpj }) => {
  const { data } = await AuthenticatedHttp.post('/businesses/validate/', {
    cnpj: sanitizeCnpj(cnpj),
  });

  if (data?.valid === false) {
    const error = new Error(BUSINESS_GENERIC_ERROR_MESSAGE);
    error.response = { status: 400, data: { ...data.errors, detail: data.detail } };
    throw error;
  }

  return data;
};

export const BUSINESS_CREATION_DISABLED_MESSAGE =
  'A criação de novos negócios ainda não está liberada.';

const BUSINESS_GENERIC_ERROR_MESSAGE =
  'Não foi possível enviar o cadastro agora. Tente novamente.';

// A criacao inteira e fechada pela flag: sem ela, criar, validar e excluir
// respondem 404 com "Negocio nao encontrado", que confundiria o produtor.
export const isBusinessCreationDisabledError = (error) => error?.response?.status === 404;

// Os erros de CNPJ chegam com o campo preenchido para aparecer embaixo do
// input, como pede o 4.2.1 da historia. O resto vem so em detail.
export const getBusinessFieldErrors = (error) => {
  const data = error?.response?.data;

  if (!data || typeof data !== 'object') {
    return {};
  }

  return Object.entries(data).reduce((fields, [field, value]) => {
    if (field === 'detail' || field === 'code' || !Array.isArray(value) || !value.length) {
      return fields;
    }

    return { ...fields, [field]: String(value[0]) };
  }, {});
};

export const getBusinessErrorMessage = (error) =>
  (isBusinessCreationDisabledError(error) ? BUSINESS_CREATION_DISABLED_MESSAGE : null) ||
  error?.response?.data?.detail ||
  error?.message ||
  BUSINESS_GENERIC_ERROR_MESSAGE;

// Devolve mensagem de bloco apenas quando nao ha erro de campo: com campo, quem
// mostra e o proprio input.
export const getBusinessValidateErrorMessage = (error) => {
  if (Object.keys(getBusinessFieldErrors(error)).length) {
    return null;
  }

  return getBusinessErrorMessage(error);
};

export const BUSINESS_ACCEPTED_FILE_TYPE = 'application/pdf';

export const BUSINESS_MAX_FILE_SIZE = 10 * 1024 * 1024;

export const isAcceptedBusinessFile = (file) =>
  file?.type === BUSINESS_ACCEPTED_FILE_TYPE && file?.size <= BUSINESS_MAX_FILE_SIZE;

// Mesmo payload da jornada PF para PJ: o backend copia os dados do
// administrador do usuario logado, entao so a empresa e os documentos sobem.
export const createBusiness = async ({ files = [], ...payload }) => {
  const rejected = files.find((file) => !isAcceptedBusinessFile(file));

  if (rejected) {
    const error = new Error(`${rejected.name}: envie apenas arquivos PDF de até 10MB.`);
    error.isBusinessFileError = true;
    throw error;
  }

  const formData = new FormData();

  formData.append('cnpj', sanitizeCnpj(payload.cnpj));
  formData.append('companyLegalName', payload.companyName);
  formData.append('partnerDeclarationAccepted', String(payload.partnerDeclaration));

  files.forEach((file) => formData.append('files', file));

  const { data } = await AuthenticatedHttp.post('/businesses/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data;
};

export const getBusinessCreateErrorMessage = getBusinessErrorMessage;
